import { Link } from '@tanstack/react-router';
import { Plug } from 'lucide-react';

import { Button } from '~/components/ui/button';

interface ConnectChannelPlaceholderProps {
  /** Optional override for the supporting copy under the headline. */
  description?: string;
  /** E2E `data-testid` root; children derive `${testId}-...`. */
  testId?: string;
}

/**
 * Full-page empty state shown inside Experiments / Catalog-Operations pages
 * when the subsidiary has no connected channel yet. CTA bounces the user to
 * the onboarding flow where they can wire up their first data source.
 */
export function ConnectChannelPlaceholder({
  description,
  testId = 'connect-channel-placeholder',
}: ConnectChannelPlaceholderProps) {
  return (
    <div
      data-testid={testId}
      className="bg-gradient-page flex h-full min-h-[60vh] flex-col items-center justify-center gap-4 p-8 text-center"
    >
      <Plug className="text-muted-foreground h-12 w-12" aria-hidden="true" />
      <div className="space-y-1.5">
        <h1 data-testid={`${testId}-title`} className="text-2xl font-semibold tracking-tight">
          Connect a channel to get started
        </h1>
        <p data-testid={`${testId}-description`} className="text-muted-foreground max-w-md text-sm">
          {description ??
            'You need to connect at least one data source before you can use this feature. Set up your first connection in onboarding.'}
        </p>
      </div>
      <Button asChild data-testid={`${testId}-onboarding-link`} className="cursor-pointer">
        <Link data-testid={`${testId}-onboarding-link`} to="/onboarding">
          Go to onboarding
        </Link>
      </Button>
    </div>
  );
}
